/**
 * Carvica — Product Configurator
 *
 * Live preview for custom house number signs and architectural signage.
 * Handles text input, material/finish preview, image upload, size preview,
 * line item properties injection, and proof approval flow.
 *
 * Initialises automatically on any page that contains a
 * `[data-product-configurator]` element.
 *
 * Vanilla ES6+ — no frameworks.
 */
;(function () {
  'use strict';

  /* ========================================================================
     MATERIAL PRESETS
     ======================================================================== */

  /**
   * Visual presets applied to the configurator preview when a material or
   * finish is selected.  Keys correspond to the option values sent from the
   * variant selector (lowercased, spaces → hyphens).
   */
  const MATERIAL_PRESETS = {
    walnut: {
      background: '#5B3A29',
      textColor:  '#F5F0E8',
      label:      'Natural Walnut',
      texture:    'linear-gradient(135deg, #5B3A29 0%, #7A5340 40%, #5B3A29 100%)',
    },
    iroko: {
      background: '#A0783C',
      textColor:  '#FFFFFF',
      label:      'African Iroko',
      texture:    'linear-gradient(135deg, #A0783C 0%, #C29A5B 40%, #A0783C 100%)',
    },
    'frosted-acrylic': {
      background: 'rgba(255,255,255,0.75)',
      textColor:  '#1A1A1A',
      label:      'Frosted Acrylic',
      texture:    'linear-gradient(135deg, rgba(255,255,255,0.85) 0%, rgba(230,235,240,0.9) 100%)',
    },
    'matte-acrylic': {
      background: '#2C2C2C',
      textColor:  '#FFFFFF',
      label:      'Matte Acrylic',
      texture:    'linear-gradient(135deg, #2C2C2C 0%, #3D3D3D 50%, #2C2C2C 100%)',
    },
    'satin-plexiglass': {
      background: '#E8E8E8',
      textColor:  '#1A1A1A',
      label:      'Satin Plexiglass',
      texture:    'linear-gradient(135deg, #E8E8E8 0%, #F5F5F5 50%, #E8E8E8 100%)',
    },
  };

  /**
   * Default size presets (width × height in px for the preview element).
   * Keys match lowercased size option values.
   */
  const SIZE_PRESETS = {
    small:  { width: 200, height: 140 },
    medium: { width: 280, height: 200 },
    large:  { width: 380, height: 260 },
  };

  /* ========================================================================
     ALLOWED UPLOAD TYPES & LIMITS
     ======================================================================== */

  const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/png', 'image/svg+xml', 'application/pdf'];
  const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.svg', '.pdf'];
  const MAX_FILE_SIZE_MB   = 10;
  const MAX_FILE_SIZE      = MAX_FILE_SIZE_MB * 1024 * 1024;

  /* ========================================================================
     PRODUCT CONFIGURATOR CLASS
     ======================================================================== */

  class ProductConfigurator {
    /**
     * @param {HTMLElement} root – the [data-product-configurator] container
     */
    constructor(root) {
      this.root = root;

      // State
      this.state = {
        houseNumber: '',
        surname:     '',
        material:    Object.keys(MATERIAL_PRESETS)[0],
        size:        'medium',
        uploadedFile:     null,
        uploadedFileUrl:  null,
        proofApproved:    false,
      };

      // Cache DOM references
      this.preview        = root.querySelector('[data-configurator-preview]');
      this.previewText    = root.querySelector('[data-configurator-preview-text]');
      this.previewNumber  = root.querySelector('[data-configurator-preview-number]');
      this.previewSurname = root.querySelector('[data-configurator-preview-surname]');
      this.previewUpload  = root.querySelector('[data-configurator-preview-upload]');
      this.fileInput      = root.querySelector('[data-configurator-file]');
      this.fileNameEl     = root.querySelector('[data-configurator-file-name]');
      this.proofCheckbox  = root.querySelector('[data-configurator-proof]');
      this.proofError     = root.querySelector('[data-configurator-proof-error]');
      this.form           = root.closest('form') || root.querySelector('form');

      this.bindEvents();
      this.applyMaterial(this.state.material);
      this.applySize(this.state.size);
    }

    /* ------------------------------------------------------------------
       EVENT BINDING
       ------------------------------------------------------------------ */

    bindEvents() {
      // Text inputs: [data-configurator-input="house-number"] etc.
      this.root.querySelectorAll('[data-configurator-input]').forEach((input) => {
        input.addEventListener('input', () => this.onTextInput(input));
      });

      // Material / finish changes from variant selector
      document.addEventListener('carvica:variant:change', (e) => {
        this.onVariantChange(e.detail.variant);
      });

      // Also listen for explicit material radio/select inside configurator
      this.root.querySelectorAll('[data-configurator-material]').forEach((el) => {
        el.addEventListener('change', () => {
          const val = el.value || el.dataset.configuratorMaterial;
          this.applyMaterial(val);
        });
      });

      // Size selector inside configurator
      this.root.querySelectorAll('[data-configurator-size]').forEach((el) => {
        el.addEventListener('change', () => {
          const val = el.value || el.dataset.configuratorSize;
          this.applySize(val);
        });
      });

      // File upload
      if (this.fileInput) {
        this.fileInput.addEventListener('change', (e) => this.onFileUpload(e));
      }

      // Proof approval checkbox
      if (this.proofCheckbox) {
        this.proofCheckbox.addEventListener('change', () => {
          this.state.proofApproved = this.proofCheckbox.checked;
          if (this.proofError) {
            this.proofError.style.display = this.state.proofApproved ? 'none' : '';
          }
        });
      }

      // Intercept form submission to inject properties & validate
      if (this.form) {
        this.form.addEventListener('submit', (e) => this.onFormSubmit(e));
      }
    }

    /* ------------------------------------------------------------------
       1. TEXT PREVIEW
       ------------------------------------------------------------------ */

    onTextInput(input) {
      const field = input.dataset.configuratorInput;

      if (field === 'house-number') {
        this.state.houseNumber = input.value;
        this.renderNumberPreview();
      } else if (field === 'surname') {
        this.state.surname = input.value;
        this.renderSurnamePreview();
      }
    }

    /**
     * Render house number in the preview area.  Auto-scales font size so the
     * text fits within the preview container.
     */
    renderNumberPreview() {
      const el = this.previewNumber || this.previewText;
      if (!el) return;

      el.textContent = this.state.houseNumber || '';
      this.autoScaleText(el);
    }

    /**
     * Render surname / additional text in the preview area.
     */
    renderSurnamePreview() {
      const el = this.previewSurname;
      if (!el) return;

      el.textContent = this.state.surname || '';
      this.autoScaleText(el);
    }

    /**
     * Shrink or grow font size so the text element fits its parent container
     * without overflow.
     */
    autoScaleText(el) {
      if (!el || !this.preview) return;

      const maxWidth  = this.preview.clientWidth * 0.85;
      const maxFontSize = 72; // px
      const minFontSize = 10;

      let fontSize = maxFontSize;
      el.style.fontSize = `${fontSize}px`;

      // Reduce until it fits
      while (el.scrollWidth > maxWidth && fontSize > minFontSize) {
        fontSize -= 1;
        el.style.fontSize = `${fontSize}px`;
      }
    }

    /* ------------------------------------------------------------------
       2. MATERIAL / FINISH PREVIEW
       ------------------------------------------------------------------ */

    /**
     * Detect material from a variant object.  Looks for common option names
     * ("Material", "Finish", "Color") and normalises the value to a key.
     */
    onVariantChange(variant) {
      if (!variant) return;

      // Check each option for a material match
      for (let i = 1; i <= 3; i++) {
        const val = variant[`option${i}`];
        if (!val) continue;
        const key = this.normaliseMaterialKey(val);
        if (MATERIAL_PRESETS[key]) {
          this.applyMaterial(key);
        }
        // Also check for size
        const sizeKey = val.toLowerCase().trim();
        if (SIZE_PRESETS[sizeKey]) {
          this.applySize(sizeKey);
        }
      }
    }

    normaliseMaterialKey(value) {
      return (value || '').toLowerCase().trim().replace(/\s+/g, '-');
    }

    applyMaterial(key) {
      const preset = MATERIAL_PRESETS[this.normaliseMaterialKey(key)];
      if (!preset || !this.preview) return;

      this.state.material = this.normaliseMaterialKey(key);
      this.preview.style.background = preset.texture || preset.background;
      this.preview.style.color      = preset.textColor;

      // Update child text elements as well
      [this.previewNumber, this.previewSurname, this.previewText].forEach((el) => {
        if (el) el.style.color = preset.textColor;
      });
    }

    /* ------------------------------------------------------------------
       3. IMAGE UPLOAD PREVIEW
       ------------------------------------------------------------------ */

    onFileUpload(e) {
      const file = e.target.files?.[0];
      if (!file) return;

      // Validate type
      const extension = '.' + file.name.split('.').pop().toLowerCase();
      if (!ALLOWED_FILE_TYPES.includes(file.type) && !ALLOWED_EXTENSIONS.includes(extension)) {
        this.showUploadError(`Invalid file type. Accepted: ${ALLOWED_EXTENSIONS.join(', ')}`);
        this.fileInput.value = '';
        return;
      }

      // Validate size
      if (file.size > MAX_FILE_SIZE) {
        this.showUploadError(`File too large. Maximum size: ${MAX_FILE_SIZE_MB}MB.`);
        this.fileInput.value = '';
        return;
      }

      this.state.uploadedFile = file;

      // Show file name
      if (this.fileNameEl) {
        this.fileNameEl.textContent = file.name;
        this.fileNameEl.classList.add('is-visible');
      }

      // Preview image in configurator (only for raster images)
      if (file.type.startsWith('image/') && file.type !== 'image/svg+xml') {
        const reader = new FileReader();
        reader.onload = (ev) => {
          this.state.uploadedFileUrl = ev.target.result;
          this.renderUploadPreview();
        };
        reader.readAsDataURL(file);
      } else {
        // For SVG / PDF we just show the file name
        this.state.uploadedFileUrl = null;
        this.renderUploadPreview();
      }
    }

    renderUploadPreview() {
      if (!this.previewUpload) return;

      if (this.state.uploadedFileUrl) {
        this.previewUpload.innerHTML = `<img src="${this.state.uploadedFileUrl}" alt="Uploaded preview" class="configurator__upload-img">`;
      } else if (this.state.uploadedFile) {
        this.previewUpload.innerHTML = `<span class="configurator__upload-name">${this.escapeHtml(this.state.uploadedFile.name)}</span>`;
      } else {
        this.previewUpload.innerHTML = '';
      }
    }

    showUploadError(message) {
      if (window.Carvica?.Toast) {
        window.Carvica.Toast.show(message, 'error');
      } else {
        alert(message); // eslint-disable-line no-alert
      }
    }

    /* ------------------------------------------------------------------
       4. SIZE PREVIEW
       ------------------------------------------------------------------ */

    applySize(key) {
      const sizeKey = (key || '').toLowerCase().trim();
      const preset  = SIZE_PRESETS[sizeKey];
      if (!preset || !this.preview) return;

      this.state.size = sizeKey;
      this.preview.style.width  = `${preset.width}px`;
      this.preview.style.height = `${preset.height}px`;

      // Re-scale text to fit new dimensions
      if (this.previewNumber || this.previewText) {
        this.autoScaleText(this.previewNumber || this.previewText);
      }
      if (this.previewSurname) {
        this.autoScaleText(this.previewSurname);
      }
    }

    /* ------------------------------------------------------------------
       5. LINE ITEM PROPERTIES & FORM SUBMISSION
       ------------------------------------------------------------------ */

    /**
     * Intercept the product form submission to:
     *   a) Validate proof approval
     *   b) Inject configurator values as hidden inputs (line item properties)
     */
    onFormSubmit(e) {
      // Validate proof approval
      if (this.proofCheckbox && !this.state.proofApproved) {
        e.preventDefault();
        e.stopImmediatePropagation();
        if (this.proofError) {
          this.proofError.textContent =
            'Please confirm you understand the designer proof process before adding to cart.';
          this.proofError.style.display = '';
        }
        this.proofCheckbox.focus();
        return;
      }

      // Inject / update hidden property inputs
      this.injectProperties();
    }

    /**
     * Create or update hidden inputs inside the form so Shopify stores
     * them as line item properties.
     */
    injectProperties() {
      if (!this.form) return;

      const props = {
        'House Number': this.state.houseNumber,
        'Surname':      this.state.surname,
        'Material':     this.state.material,
        'Size':         this.state.size,
        'Designer Proof':
          'Designer proof sent before production',
      };

      /*
       * NOTE: Shopify's native cart does not support direct file upload via
       * line item properties.  To attach customer-uploaded files to an order
       * you need a file-upload app such as Uploadery, Upload-Lift, or a
       * custom solution that stores the file and returns a URL which is then
       * saved as a property value.
       *
       * Below we store the file *name* so the merchant sees what the customer
       * intended to upload.  The actual file should be handled by the upload
       * app's workflow.
       */
      if (this.state.uploadedFile) {
        props['Uploaded File'] = this.state.uploadedFile.name;
      }

      for (const [label, value] of Object.entries(props)) {
        if (!value) continue; // skip empty values

        const inputName = `properties[${label}]`;
        let input = this.form.querySelector(`input[name="${inputName}"]`);

        if (!input) {
          input = document.createElement('input');
          input.type  = 'hidden';
          input.name  = inputName;
          this.form.appendChild(input);
        }

        input.value = value;
      }
    }

    /* ------------------------------------------------------------------
       HELPERS
       ------------------------------------------------------------------ */

    escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }
  }

  /* ========================================================================
     AUTO-INITIALISE
     ======================================================================== */

  function init() {
    document.querySelectorAll('[data-product-configurator]').forEach((el) => {
      // Avoid double-initialisation
      if (el._carvicaConfigurator) return;
      el._carvicaConfigurator = new ProductConfigurator(el);
    });
  }

  document.addEventListener('DOMContentLoaded', init);

  // Expose class for external use (e.g. qr.js integration)
  window.Carvica = window.Carvica || {};
  window.Carvica.ProductConfigurator = ProductConfigurator;
})();
